import { AlertCircle, AlertTriangle, CircleCheck, Clock, DatabaseZap, DollarSign, Zap } from "lucide-react"
import type { CommentContext, GroupRenderer, ResultGroup } from "../../events/messageGroups"
import { formatInputCacheRate, normalizedCacheReadTokens } from "../../events/usage"
import { ActivityEventsContent } from "./activityRenderer"

const RESULT_DISPLAY_NAMES: Record<ResultGroup["subtype"], string> = {
    success: "Completed",
    done_with_gaps: "Completed with gaps",
    error_during_execution: "Error",
    error_max_turns: "Max Turns",
    error_max_budget_usd: "Budget Exceeded",
    error_max_structured_output_retries: "Output Error",
}

export type ResultHeaderFact = { kind: "duration" | "cost" | "tokens" | "cache"; label: string }

function formatDuration(durationMs: number): string | null {
    if (durationMs <= 0) return null
    const seconds = durationMs / 1000
    return seconds < 60 ? `${seconds.toFixed(1)}s` : `${Math.floor(seconds / 60)}m ${(seconds % 60).toFixed(0)}s`
}

function formatCost(totalCostUsd: number): string | null {
    if (totalCostUsd <= 0) return null
    if (totalCostUsd < 0.0001) return "<$0.0001"
    return totalCostUsd < 0.01 ? `$${totalCostUsd.toFixed(4)}` : `$${totalCostUsd.toFixed(2)}`
}

export function resultHeaderFacts(group: ResultGroup): ResultHeaderFact[] {
    const duration = formatDuration(group.durationMs)
    const cost = formatCost(group.totalCostUsd)
    const totalTokens = group.usage.inputTokens + group.usage.outputTokens
    const cacheRate = formatInputCacheRate(group.usage)
    return [
        duration ? { kind: "duration", label: duration } : null,
        cost ? { kind: "cost", label: cost } : null,
        totalTokens > 0 ? { kind: "tokens", label: `${totalTokens.toLocaleString()} tokens` } : null,
        cacheRate ? { kind: "cache", label: `${cacheRate} input cache` } : null,
    ].filter((fact): fact is ResultHeaderFact => fact !== null)
}

export function formatResultLabel(group: ResultGroup): string {
    const displayName = group.isError && (group.subtype === "success" || group.subtype === "done_with_gaps") ? "Error" : RESULT_DISPLAY_NAMES[group.subtype]
    const duration = formatDuration(group.durationMs)
    const cost = formatCost(group.totalCostUsd)
    return [displayName, duration, cost].filter(Boolean).join(" · ")
}

function ResultContent({ group }: { group: ResultGroup; ctx: CommentContext }) {
    const cacheRate = formatInputCacheRate(group.usage)
    const cacheReadTokens = normalizedCacheReadTokens(group.usage)

    return (
        <div className="px-3 py-2 bg-base-100 space-y-2">
            <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
                <div>
                    <span className="text-muted">Input tokens:</span>{" "}
                    <span className="text-base-content font-mono">{group.usage.inputTokens.toLocaleString()}</span>
                </div>
                <div>
                    <span className="text-muted">Output tokens:</span>{" "}
                    <span className="text-base-content font-mono">{group.usage.outputTokens.toLocaleString()}</span>
                </div>
                {cacheRate && cacheReadTokens !== undefined && (
                    <div>
                        <span className="text-muted">Input cache rate:</span>{" "}
                        <span className="text-base-content font-mono">
                            {cacheRate} ({cacheReadTokens.toLocaleString()} cached)
                        </span>
                    </div>
                )}
            </div>
            {group.result && (
                <div>
                    <div className="text-xs text-muted font-medium mb-1">Result</div>
                    <pre className="text-xs p-2 bg-base-200 border border-border whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto">
                        {group.result}
                    </pre>
                </div>
            )}
            {group.errors && group.errors.length > 0 && (
                <div>
                    <div className="text-xs text-error font-medium mb-1">Errors</div>
                    <pre className="text-xs p-2 bg-error/10 border border-error/20 whitespace-pre-wrap overflow-x-auto max-h-48 overflow-y-auto text-error">
                        {group.errors.join("\n")}
                    </pre>
                </div>
            )}
            {group.trailingActivity && group.trailingActivity.length > 0 && (
                <div className="border-t border-border pt-2">
                    <div className="mb-1 text-xs font-medium text-muted">Turn activity</div>
                    <ActivityEventsContent events={group.trailingActivity} />
                </div>
            )}
        </div>
    )
}

export const resultRenderer: GroupRenderer<ResultGroup> = {
    getLabel: formatResultLabel,
    getIcon: (group) => {
        if (group.isError) return <AlertCircle size="0.85em" className="text-error flex-shrink-0" />
        if (group.subtype === "done_with_gaps") return <AlertTriangle size="0.85em" className="text-warning flex-shrink-0" />
        return <CircleCheck size="0.85em" className="text-success flex-shrink-0" />
    },
    getStatusIcon: (group) => {
        if (group.isError) return <AlertCircle size="1em" className="text-error flex-shrink-0" />
        if (group.subtype === "done_with_gaps") return <AlertTriangle size="1em" className="text-warning flex-shrink-0" />
        return null
    },
    getHeaderInfo: (group) => {
        const facts = resultHeaderFacts(group)
        if (facts.length === 0) return null

        return (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
                {facts.map((fact) => (
                    <span key={fact.kind} className="flex items-center gap-1">
                        {fact.kind === "duration" && <Clock size="0.85em" />}
                        {fact.kind === "cost" && <DollarSign size="0.85em" />}
                        {fact.kind === "tokens" && <Zap size="0.85em" />}
                        {fact.kind === "cache" && <DatabaseZap size="0.85em" />}
                        {fact.label}
                    </span>
                ))}
            </div>
        )
    },
    renderContent: (group, ctx) => <ResultContent group={group} ctx={ctx} />,
}
